import { useCallback } from 'react';
import { useMemo } from 'react';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { getMetricByNameFromObservation } from 'libs/end-to-end/src/me';
import { getMetricsByFocusAreaFromObservation } from 'libs/end-to-end/src/me';
import { getMetricDisplayNameFromObservation } from 'libs/end-to-end/src/me';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';

export type GetMetricDisplayNameFn = (params: {
  focusArea: FocusArea;
  metricName: string;
}) => string | null;

export type GetMetricsByFocusAreaFn = (params: {
  focusArea: FocusArea;
}) => MyMetricDefinition[];

export type GetMetricByNameFn = (params: {
  focusArea: FocusArea;
  metricName: string;
  myObservation: MyObservation | null;
}) => MyMetricDefinition | undefined;

export type GetMetricAndFocusAreaFromColumnNameFn = (params: {
  columnName: string;
}) => {
  focusArea: FocusArea;
  metric: MyMetricDefinition;
} | null;

export type UseMetricConfigPayload = {
  getMetricDisplayName: GetMetricDisplayNameFn;
  getMetricByName: GetMetricByNameFn;
  getMetricsByFocusArea: GetMetricsByFocusAreaFn;
  getMetricAndFocusAreaFromColumnName: GetMetricAndFocusAreaFromColumnNameFn;
};

export const useMetricConfig = (): UseMetricConfigPayload => {
  const { myObservation } = useObservation();

  const getMetricByName = useCallback(
    ({
      metricName,
      focusArea,
    }: {
      metricName: string;
      focusArea: FocusArea;
    }): MyMetricDefinition | undefined => {
      return getMetricByNameFromObservation({
        metricName,
        focusArea,
        myObservation,
      });
    },
    [myObservation],
  );

  const getMetricsByFocusArea = useCallback(
    ({ focusArea }: { focusArea: FocusArea }): MyMetricDefinition[] => {
      return getMetricsByFocusAreaFromObservation({
        focusArea,
        myObservation,
      });
    },
    [myObservation],
  );

  /**
   * Returns the display name for a metric in a particular focus area.
   * @param focusArea - the focus area
   * @param metricName - the metric name
   * @returns the display name of the metric
   */
  const getMetricDisplayName = useCallback(
    ({ focusArea, metricName }: { focusArea: FocusArea; metricName: string }) =>
      getMetricDisplayNameFromObservation({
        focusArea,
        metricName,
        myObservation,
      }),
    [myObservation],
  );

  const getMetricAndFocusAreaFromColumnName = useCallback(
    ({
      columnName,
    }: {
      columnName: string;
    }): {
      focusArea: FocusArea;
      metric: MyMetricDefinition;
    } | null => {
      if (!myObservation) {
        return null;
      }

      // check if column name is a metric
      for (const fa of myObservation.focusAreas) {
        const metric = fa.metrics.find(
          (m) => m.columnHeaderValue === columnName,
        );

        if (metric) {
          return {
            focusArea: fa.focusArea,
            metric,
          };
        }
      }

      return null;
    },
    [myObservation],
  );

  return useMemo(
    () => ({
      getMetricDisplayName,
      getMetricsByFocusArea,
      getMetricByName,
      getMetricAndFocusAreaFromColumnName,
    }),
    [
      getMetricDisplayName,
      getMetricsByFocusArea,
      getMetricByName,
      getMetricAndFocusAreaFromColumnName,
    ],
  );
};
